import React from 'react';
import classnames from 'classnames/bind';
import Image from 'next/image';
import { Price as PriceInterface, Product, SearchService } from '@services';
import { Button, Price } from '@components';
import { getImageTransformationByProductType,
  AnalyticsListContext, pushSelectItemEvent, pushAddToCartEvent, UserContext } from '@utils';
import { ROUTES } from '@constants';
import NoImagePlaceholder from '@assets/images/no-image-placeholder.png';
import styles from './ProductCardBase.module.scss';
import { ProductCardBaseSkeleton } from './ProductCardBase.skeleton';
import { useRouter } from 'next/router';
import { useServiceRef } from 'hooks';
import { ImageTransformation, TRANSFORMATIONS } from '@services/image_transformation';
import { Variant } from '@services/product/interfaces';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { Icon } from 'gd-design-library';

const CN = 'product-card-base';
const actionAreaClassName = `${CN}--action-area`;
const mediaClassName = `${actionAreaClassName}--media`;
const mediaWrapperClassName = `${actionAreaClassName}--media-wrapper`;
const contentClassName = `${actionAreaClassName}--content`;
const hoveredClassName = `${actionAreaClassName}--hovered`;
const titleClassName = `${contentClassName}--title`;
const addButtonClassName = `${CN}--add-button`;

const cx = classnames.bind(styles);

interface ProductCardBaseProps {
  product: Product;
  className?: string;
  isLoading?: boolean;
  search?: boolean;
  position?: number;
  images?: string[];
  price?: PriceInterface;
  isHovered?: boolean;
  analyticsContext: AnalyticsListContext;
  handleAddToCart?: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  selectedVariant?: Variant;
}

export const ProductCardBase: React.FC<ProductCardBaseProps> = ({
  product,
  className,
  isLoading,
  search=false,
  position=0,
  price,
  images,
  isHovered,
  analyticsContext,
  handleAddToCart,
  selectedVariant
}) => {
  const router = useRouter();
  const searchServiceRef = useServiceRef(SearchService);
  const userContext = React.useContext(UserContext);
  const [ imageError, setImageError ] = React.useState(false);
  const [ isAddingToCart, setIsAddingToCart ] = React.useState(false);

  React.useEffect(() => {
    setImageError(false);
  }, [ images, product.image ]);

  const onClick = (e:React.MouseEvent<HTMLElement>):void => {
    e.preventDefault();
    if (search) {
      searchServiceRef.current.sendClickEvent([ product.id ], userContext.user?.id, [ position + 1 ]);
    }
    pushSelectItemEvent(product, analyticsContext);
    router.push(`${ROUTES.products}/${product.id}`);
  };

  const handleAddButtonClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const variantId = selectedVariant?.id || product.matchingVariantId;
    if (!handleAddToCart || !variantId) return;

    setIsAddingToCart(true);
    try {
      const lineItemDraft: LineItemDraft = {
        productId: product.id,
        variantId,
        quantity: 1
      };
      await handleAddToCart(lineItemDraft, price?.currencyCode);
      pushAddToCartEvent(product, 1);
    } finally {
      setIsAddingToCart(false);
    }
  };
  const imageTransformation = new ImageTransformation();

  const handleImageError = (): void => {
    setImageError(true);
  };

  const getImageSrc = (): string => {
    if (imageError) {
      return NoImagePlaceholder.src;
    }

    if (images?.length) {
      return imageTransformation.transform(
        images[0],
        [ ...getImageTransformationByProductType(product.productType), {
          name: TRANSFORMATIONS.AUTO_SIZE,
          params: { width: 320 }
        } ]
      );
    }

    if (product.image?.length) {
      return imageTransformation.transform(
        product.image,
        [ ...getImageTransformationByProductType(product.productType), {
          name: TRANSFORMATIONS.AUTO_SIZE,
          params: { width: 320 }
        } ]
      );
    }

    return NoImagePlaceholder.src;
  };

  return (
    !isLoading ? (
      <div className={ cx(CN, className) }>
        <a href={ `${ROUTES.products}/${product.id}` } onClick={ onClick }>
          <div className={ cx(actionAreaClassName) }>
            <div className={ cx(mediaWrapperClassName) }>
              <Image
                className={ cx(mediaClassName) }
                src={ getImageSrc() }
                alt={ product?.name || 'Product image' }
                width={ 320 }
                height={ 0 }
                sizes='(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw'
                loading='lazy'
                onError={ handleImageError }
                unoptimized={ imageError }
              />
              { handleAddToCart && (selectedVariant?.id || product.matchingVariantId) && (
                <Button
                  className={ cx(addButtonClassName) }
                  onClick={ handleAddButtonClick }
                  disabled={ isAddingToCart }
                  aria-label='Add to cart'
                  iconStart={ <Icon name='plus' width={ 14 } height={ 14 } /> }
                >
                  { isAddingToCart ? '...' : 'Add' }
                </Button>
              )}
            </div>
            <div className={ cx(contentClassName, { [hoveredClassName]: isHovered }) }>
              <div className={ cx(titleClassName) }>
                { product.name?.toLowerCase() }
              </div>
              <Price price={ price ? price : product.price }/>
            </div>
          </div>
        </a>
      </div>
    ) : <ProductCardBaseSkeleton />
  );
};
